import { fetchAccumulatedRatesForBedbankOffer, getBedbankRateKey } from 'actions/BedbankOfferActions'
import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from './reduxHooks'
import useMetasearchSourceTransition from 'hooks/OfferPage/useMetasearchSourceTransition'
import { uniqueBy } from 'lib/array/arrayUtils'
import { serialiseOccupancy } from 'lib/search/searchUtils'

const emptyRates: Array<App.BedbankRate> = []

function useBedbankRates(
  offerId: string | undefined,
  occupants?: Array<App.Occupants>,
  checkIn?: string,
  checkOut?: string,
  hookEnabled: boolean = true,
): [{ hotelOnlyRates: Array<App.BedbankRate>, hotelBundleRates: Array<App.BedbankRate> }, boolean] {
  const dispatch = useAppDispatch()
  const filterKey = checkIn && checkOut && occupants ? getBedbankRateKey(occupants, checkIn, checkOut) : ''
  const hasTransitionedFromMetasearch = useMetasearchSourceTransition()

  useEffect(() => {
    // We can't get rates for a bedbank property without the following properties
    if (hookEnabled && checkIn && checkOut && occupants && offerId) {
      dispatch(fetchAccumulatedRatesForBedbankOffer(offerId, occupants, checkIn, checkOut, hasTransitionedFromMetasearch))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, offerId, hookEnabled, hasTransitionedFromMetasearch])

  const packages = useAppSelector(state => state.offer.bedbankOfferRates[offerId ?? '']?.[filterKey]) ?? emptyRates
  const fetching = useAppSelector(state => state.offer.offerRatesLoading[offerId ?? '']) ?? !!(hookEnabled && filterKey)

  const rates = useMemo(() => {
    const hotelOnlyRates = packages.filter(p => !p.isFlightBundle)
    const hotelBundleRates = packages.filter(p => p.isFlightBundle)

    return {
      hotelOnlyRates,
      hotelBundleRates,
    }
  }, [packages])

  return [rates, fetching]
}

export default useBedbankRates

export function useBedbankRatesForIndividualRooms(
  offerId: string | undefined,
  occupants: Array<App.Occupants>,
  checkIn?: string,
  checkOut?: string,
  hookEnabled: boolean = true,
): [Array<{ hotelOnlyRates: Array<App.BedbankRate>, hotelBundleRates: Array<App.BedbankRate> }>, boolean] {
  const dispatch = useAppDispatch()
  const filterKeys = useMemo(() => occupants.map(room => (
    checkIn && checkOut ? getBedbankRateKey([room], checkIn, checkOut) : ''
  )), [checkIn, checkOut, occupants])
  const hasTransitionedFromMetasearch = useMetasearchSourceTransition()

  useEffect(() => {
    // We can't get rates for a bedbank property without the following properties
    if (hookEnabled && checkIn && checkOut && offerId) {
      const uniqueRooms = uniqueBy(occupants, room => serialiseOccupancy([room]))
      uniqueRooms.forEach(room => (
        dispatch(fetchAccumulatedRatesForBedbankOffer(offerId, [room], checkIn, checkOut, hasTransitionedFromMetasearch))
      ))
    }
  }, [filterKeys, offerId, hookEnabled, hasTransitionedFromMetasearch, checkIn, checkOut, occupants, dispatch])

  const packagesByRateKey = useAppSelector(state => state.offer.bedbankOfferRates[offerId ?? ''])
  const fetchingByRateKey = useAppSelector(state => state.offer.bedbankOfferRatesLoading[offerId ?? ''])

  const rates = useMemo(() => (
    filterKeys.map(key => {
      const packages = packagesByRateKey?.[key] ?? emptyRates
      const hotelOnlyRates = packages.filter(p => !p.isFlightBundle)
      const hotelBundleRates = packages.filter(p => p.isFlightBundle)

      return {
        hotelOnlyRates,
        hotelBundleRates,
      }
    })
  ), [filterKeys, packagesByRateKey])

  const fetching = useMemo(() => (
    filterKeys.some(key => fetchingByRateKey?.[key] ?? !!(hookEnabled && key))
  ), [fetchingByRateKey, filterKeys, hookEnabled])

  return [rates, fetching]
}
